import React, { useEffect } from "react";
import { ArrowLeft, Sparkles, PenLine } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSidebar } from "../../../Components/ui/sidebarNew";

export default function JobPostMain({ setTab, editCampaignData, isEditMode }) {
  const navigate = useNavigate();
  const { state } = useSidebar();

  useEffect(() => {
    if (isEditMode && editCampaignData) {
      navigate("/brand/jobpostbrief", {
        state: { briefData: editCampaignData, isEditMode: true },
      });
    }
  }, [isEditMode, editCampaignData, navigate]);

  const createEmptyBrief = () => {
    const emptyBriefData = {
      user_id: "",
      description: "",
      title: "",
      apply_by_date: "",
      scope_of_work: "",
      delivery_date: "",
      delivery_info: "",
      compensation: "",
      platform: "",
      category: "",
      reach_min: 10000,
      location: "",
      additional_info: "",
      source: "manual",
    };

    navigate("/brand/jobpostbrief", {
      state: { briefData: emptyBriefData, fromManual: true, isEditMode: false },
    });
  };

  const handleGetStartedWithAI = () => {
    navigate("/brand/jobpostdescription");
  };

  return (
    <div
      style={{
        marginLeft: state === "collapsed" ? "28px" : "",
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 100px)",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        {/* Header Section */}
        <header 
          className="flex pb-4 flex-col md:flex-row md:items-center md:justify-between gap-3" 
          style={{ flexShrink: 0, zIndex: 20, position: "relative" }}
        >
          {/* Back Button */}
          <button
            onClick={() => setTab("main")}
            className="flex h-[38px] px-5 justify-center items-center rounded-full text-sm font-medium cursor-pointer transition-all duration-200 bg-gray-50 text-gray-600 hover:bg-green-50 hover:text-green-700 border-none"
            style={{
              boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
            }}
          >
            <ArrowLeft strokeWidth={2.2} size={18} className="mr-2" />
            Back
          </button>
        </header>

        {/* Main Content Container */}
        <div
          className="rounded-xl bg-white overflow-hidden w-full"
          style={{
            boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
            border: "1px solid rgba(16, 185, 129, 0.1)",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <div 
            className="flex flex-col justify-center items-center text-center p-8"
            style={{ 
              flex: 1, 
              overflowY: "auto",
              background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
            }}
          >
            <div className="max-w-2xl w-full flex flex-col items-center">
              {/* Image with modern container */}
              <div 
                className="mb-10 rounded-2xl overflow-hidden"
                style={{
                  boxShadow: "0px 4px 12px 0px rgba(16, 185, 129, 0.15)",
                  border: "1px solid rgba(16, 185, 129, 0.1)",
                }}
              >
                <img
                  alt="Campaign Creation"
                  className="h-32 w-44 object-cover"
                  src={`${process.env.PUBLIC_URL}/images/job_campaign_main.jpeg`}
                />
              </div>

              {/* Title */}
              <h1 
                className="text-4xl md:text-5xl font-bold mb-4"
                style={{ 
                  color: "#1F2937",
                  lineHeight: 1.2,
                }}
              >
                Ready to launch your{" "}
                <span style={{ color: "#10B981" }}>campaign</span>?
              </h1>

              {/* Subtitle */}
              <p 
                className="text-base md:text-lg mb-10 max-w-lg"
                style={{ color: "#64748B" }}
              >
                Connect with awesome creators faster than ever.{" "}
                <br className="hidden md:block" />
                Let AI help you craft the perfect campaign brief!
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center gap-4">
                {/* Primary AI Button */}
                <button
                  onClick={handleGetStartedWithAI}
                  className="flex h-[48px] px-8 items-center gap-3 rounded-full text-base font-semibold cursor-pointer border-none text-white transition-all duration-200 hover:opacity-90 hover:scale-[1.02]"
                  style={{
                    background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                    boxShadow: "0px 4px 12px 0px rgba(16, 185, 129, 0.35)",
                  }}
                >
                  <Sparkles strokeWidth={2.2} size={20} />
                  <span>Get started with AI</span>
                </button>

                {/* Secondary Manual Button */}
                <button
                  onClick={createEmptyBrief}
                  className="flex h-[48px] px-8 items-center gap-3 rounded-full text-base font-semibold cursor-pointer transition-all duration-200 hover:bg-green-50"
                  style={{
                    background: "transparent",
                    border: "2px solid #10B981",
                    color: "#10B981",
                    boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
                  }}
                >
                  <PenLine strokeWidth={2.2} size={20} />
                  <span>Create manually</span>
                </button>
              </div>

              {/* Decorative element */}
              <div 
                className="mt-12 flex items-center gap-3"
                style={{ color: "#9CA3AF" }}
              >
                <div style={{ width: "40px", height: "1px", background: "#E5E7EB" }} />
                <span className="text-sm">Choose your preferred method</span>
                <div style={{ width: "40px", height: "1px", background: "#E5E7EB" }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
